import {
  ClerkProvider,
  useClerk,
  useOrganizationList,
  useSession,
} from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import {
  createRootRoute,
  HeadContent,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';

import { NotFound } from '@/components/NotFound';
import { SentryUser } from '@/components/observability/SentryUser';
import { Toaster } from '@/components/ui/Toaster';
import { useScrollToHash } from '@/hooks/useScrollToHash';
import { routeNeedsClerk } from '@/lib/auth/clerkRoutes';
import {
  scopeActiveOrganizationAttempt,
  setActiveOrganizationAttempt,
} from '@/lib/organization/activeOrganizationAttempt';
import { setActiveOrganization } from '@/lib/organization/switchActiveOrganization';
import {
  isHealthy,
  useOrganizationsWithHealth,
} from '@/lib/query/useOrganizationsWithHealth';

/**
 * Root route - wraps all other routes.
 * Public routes render without Clerk. Auth and application routes mount Clerk
 * here so a slow or unavailable auth client cannot block marketing content.
 */
export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});

// Backend-orchestrated organization creation (PATCH /api/v1/onboarding) creates the Clerk
// organization via Backend API but does NOT bind it to the user's session as the active
// organization. Without an active organization, Clerk's session JWT resolves
// `{{organization.id}}`/`{{organization.role}}`/`{{organization.slug}}` template claims to null even though
// the user has the membership. The frontend's `useOrganization()` hook then
// renders the user as non-admin and admin UI is hidden. This hook auto-sets a
// membership as active whenever a signed-in user lands without one — covers new
// signups AND existing users whose session lost its active-organization binding (Clerk
// default after explicit organization delete + re-create).
//
// The choice is health-aware: picking `data[0]` blindly (undefined order) can land
// a multi-organization user on an unhealthy organization (e.g. a removed VCS installation), which the
// dashboard then bounces to /onboarding where it dead-ends on a 409. We cross-
// reference the backend's per-organization health and prefer a healthy organization, falling back to
// `data[0]` only when health is unavailable or no organization is healthy (so a single-organization
// user is never blocked by a transient health-read failure).
export function useEnsureActiveOrganization() {
  const { session } = useSession();
  const { setActive } = useClerk();
  const { userMemberships } = useOrganizationList({
    userMemberships: { infinite: false },
  });

  // Discard any attempt recorded for a different user before it can be read. A sign-out and
  // sign-in inside one page load leaves this store untouched otherwise.
  const userId = session?.user?.id ?? null;
  useEffect(() => {
    scopeActiveOrganizationAttempt(userId);
  }, [userId]);

  const needsActiveOrganization =
    !!session && !session.lastActiveOrganizationId;
  const {
    data: health,
    isLoading: healthLoading,
    isError: healthError,
  } = useOrganizationsWithHealth({ enabled: needsActiveOrganization });

  useEffect(() => {
    if (!needsActiveOrganization) return;
    const memberships = userMemberships?.data;
    if (!memberships || memberships.length === 0) return;

    // Claim the wait before reading health. The health query settles on success or error, but a
    // request that neither resolves nor rejects would otherwise hold this effect below the guard
    // with no record that anything is happening.
    setActiveOrganizationAttempt('inFlight', userId);

    // Wait for health unless it errored — then fall back to data[0] so a transient
    // health-read failure never blocks a legitimate user from getting an active organization.
    if (healthLoading && !healthError) return;

    let chosen = memberships[0];
    if (health && health.length > 0) {
      const healthyIds = new Set(
        health.filter(isHealthy).map((o) => o.clerkOrganizationId)
      );
      const healthy = memberships.find((m) =>
        healthyIds.has(m.organization.id)
      );
      if (healthy) chosen = healthy;
    }

    // Record the outcome so the dashboard can reach a terminal, actionable state rather than
    // waiting on a result nothing reports. This does not navigate: the effect runs on every
    // Clerk-mounted route, so a page change here would move users off /onboarding and every
    // other route mid-flow.
    void setActiveOrganization(setActive, chosen.organization.id).then((ok) => {
      setActiveOrganizationAttempt(ok ? 'idle' : 'failed', userId);
    });
  }, [
    userId,
    needsActiveOrganization,
    userMemberships?.data,
    health,
    healthLoading,
    healthError,
    setActive,
  ]);
}

function RootComponent() {
  useScrollToHash();
  const { pathname } = useLocation();

  const needsClerk = routeNeedsClerk(pathname);

  if (!needsClerk) {
    return <RootContents />;
  }

  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={{ baseTheme: dark }}
    >
      <ClerkRootContents />
    </ClerkProvider>
  );
}

function ClerkRootContents() {
  useEnsureActiveOrganization();

  return (
    <>
      <SentryUser />
      <RootContents />
    </>
  );
}

function RootContents() {
  // Disable devtools during E2E tests (causes strict mode violations)
  const isE2ETest = import.meta.env.VITE_E2E_TEST === 'true';

  return (
    <>
      <HeadContent />
      <Outlet />
      <Toaster />
      {import.meta.env.DEV && !isE2ETest && (
        <>
          <style>{`
            /* Offset TanStack Router devtools button to avoid overlap */
            button[aria-label*="Router"] {
              bottom: 12px !important;
              right: 75px !important;
            }
            /* Alternative selector */
            div[style*="position: fixed"][style*="bottom: 16px"][style*="right: 16px"] button:first-child {
              position: fixed !important;
              bottom: 12px !important;
              right: 75px !important;
            }
          `}</style>
          <TanStackRouterDevtools position="bottom-right" />
        </>
      )}
    </>
  );
}
